import {Image, message, Modal, InputNumber} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import {useGetOneProductQuery} from "../../RTK/Slices/Slices/apiSliceMeat";
import ServerError from "../../utils/ServerError";
import Loader2 from "../../Components/Loader2";
import {useState} from "react";
import {addToCart, isInCart, getCartItemByProductId} from "../../utils/localStorage";
import {useCart} from "../../Context/CartContext";
import {language} from "../../language/Language";
import {Helmet} from "react-helmet";

const texts = {
    ar: {
        deliveryTo: "سعر التوصيل إلى",
        deliveryTime: "وقت التوصيل:",
        hours: "ساعة",
        EGP: "جنيه",
        available: "متوفر في المخزن:",
        notAvailable: "غير متوفر في المخزن",
        inCart: "في السلة:",
        quantity: "الكمية",
        maxReached: "لقد وصلت للحد الأقصى المتاح",
        goToCart: "إذهب إلى السلة",
        addToCart: "أضف إلى السلة",
        addMore: "إضافة المزيد",
        notAvailableBtn: "غير متوفر",
        discount: "خصم",
        loginRequired: "تسجيل الدخول مطلوب",
        loginToAdd: "يجب تسجيل الدخول لإضافة المنتجات إلى السلة.",
        login: "تسجيل الدخول",
        cancel: "إلغاء",
        addedSuccess: "تمت إضافة المنتج إلى السلة بنجاح!",
        addFailed: "فشل في إضافة المنتج إلى السلة. تحقق من المخزون المتاح",
        addError: "فشل إضافة المنتج إلى السلة. يرجى المحاولة مرة أخرى.",
        stockWarning: (stock) => `الكمية المتاحة في المخزن هي ${stock} فقط`,
        stockExceeded: (q, stock) => `الكمية المطلوبة (${q}) أكبر من المتاح في المخزن (${stock})`,
        outOfStock: "هذا المنتج غير متوفر في المخزن حالياً",
    },
    en: {
        deliveryTo: "Delivery price to",
        deliveryTime: "Delivery time:",
        hours: "Hour",
        EGP: "EGP ",
        available: "Available in stock:",
        notAvailable: "Out of stock",
        inCart: "In cart:",
        quantity: "Quantity",
        maxReached: "You have reached the maximum available",
        goToCart: "Go to cart",
        addToCart: "Add to cart",
        addMore: "Add more",
        notAvailableBtn: "Not available",
        discount: "Discount",
        loginRequired: "Login required",
        loginToAdd: "You must be logged in to add products to the cart.",
        login: "Login",
        cancel: "Cancel",
        addedSuccess: "Product added to cart successfully!",
        addFailed: "Failed to add product to cart. Check available stock.",
        addError: "Failed to add product to cart. Please try again.",
        stockWarning: (stock) => `Only ${stock} available in stock`,
        stockExceeded: (q, stock) => `Requested quantity (${q}) exceeds available stock (${stock})`,
        outOfStock: "This product is currently out of stock",
    },
};
const t = texts[language === "en" ? "en" : "ar"];

const ProductDetails = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("user");
    const {updateCartCount} = useCart();

    const {
        data: productData,
        isLoading: isProdLoading,
        isError: isProdError,
    } = useGetOneProductQuery(id);

    const ProdDetails = productData?.data?.[0] ?? null;
    const availableStock = ProdDetails?.stock ? parseInt(ProdDetails.stock) : 0;

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [quantity, setQuantity] = useState(1);

    // التحقق من وجود المنتج في السلة
    const productInCart = ProdDetails ? isInCart(ProdDetails.id) : false;
    const cartItem = ProdDetails ? getCartItemByProductId(ProdDetails.id) : null;

    const handleQuantityChange = (value) => {
        if (value === null || value === undefined) {
            setQuantity(1);
            return;
        }
        if (value > availableStock) {
            message.warning(t.stockWarning(availableStock));
            setQuantity(availableStock);
            return;
        }
        if (value < 1) {
            setQuantity(1);
            return;
        }
        setQuantity(value);
    };

    const handleAddToCart = async (event) => {
        event.stopPropagation();
        if (!token) {
            setIsModalVisible(true);
            return;
        }
        if (quantity > availableStock) {
            message.error(t.stockExceeded(quantity, availableStock));
            return;
        }
        if (availableStock === 0) {
            message.error(t.outOfStock);
            return;
        }
        try {
            const productForCart = {
                product_id: ProdDetails.id,
                name: ProdDetails.name,
                price: ProdDetails.price,
                default_sales_discount: ProdDetails.default_sales_discount,
                image_url: ProdDetails.image_url,
                stock: ProdDetails.stock,
            };
            const success = addToCart(productForCart, quantity);
            if (success) {
                message.success(t.addedSuccess);
                updateCartCount();
            } else {
                message.error(t.addFailed);
            }
        } catch (error) {
            message.error(t.addError);
            console.error(t.addError, error);
        }
    };

    const handleRedirectToSignUp = () => {
        navigate("/Login");
        setIsModalVisible(false);
    };

    const renderProductDescription = () => {
        if (!ProdDetails?.product_description) return null;

        return (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mt-4">
                <div
                    className="prose prose-gray max-w-none"
                    dangerouslySetInnerHTML={{__html: ProdDetails.product_description}}
                />
            </div>
        );
    };

    if (isProdLoading) return <Loader2 />;
    if (isProdError || !ProdDetails) return <ServerError />;

    return (
        <div className="bg-gray-50 pb-[80px] min-h-screen">
            <Helmet>
                <title>Meat-House El-Fardos</title>
                <meta
                    name="description"
                    content={`تعرف على تفاصيل ${
                        ProdDetails?.name || ""
                    } : السعر، الوزن، التوصيل، والعروض في Meat House. اطلب الآن أفضل اللحوم الحلال بجودة عالية وتوصيل سريع في مصر.`}
                />
                {/* Structured Data for Product */}
                <script type="application/ld+json">
                    {`
            {
              "@context": "https://schema.org/",
              "@type": "Product",
              "name": "${ProdDetails?.name || ""}",
              "image": ["${ProdDetails?.image_url || ""}"],
              "description": "${ProdDetails?.description || ""}",
              "sku": "${ProdDetails?.sku || ProdDetails?.id || ""}",
              "brand": {
                "@type": "Brand",
                "name": "Meat House"
              },
              "offers": {
                "@type": "Offer",
                "priceCurrency": "EGP",
                "price": "${ProdDetails?.price || ""}",
                "availability": "https://schema.org/InStock"
              }
            }
          `}
                </script>
            </Helmet>
            <div className="container mt-[80px] pt-5 md:pt-10">
                <div className="grid items-start grid-cols-1 lg:grid-cols-5 gap-12">
                    {/* Product Image Section */}
                    <div className="lg:col-span-3 text-center">
                        <div className="bg-white shadow-lg border border-gray-200 px-4 py-12 rounded-xl">
                            <Image
                                src={ProdDetails?.image_url}
                                alt="Product"
                                className="!w-9/12 rounded object-cover mx-auto"
                            />
                        </div>
                    </div>

                    {/* Product Details Section */}
                    <div className="lg:col-span-2">
                        <h2 className="text-3xl font-semibold text-gray-900">{ProdDetails?.name}</h2>
                        {/* Product Status */}
                        <div className="flex flex-col gap-2">


                            {productInCart && cartItem && (
                                <p className="text-blue-600 text-sm mt-4 font-medium">
                                    {t.inCart} {cartItem.count} قطعة
                                </p>
                            )}
                        </div>
                        {/* Pricing */}
                        <div className="flex flex-wrap gap-4 mt-4">
                            {ProdDetails?.default_sales_discount && parseFloat(ProdDetails.default_sales_discount) > 0 ? (
                                <>
                                    {/* السعر بعد الخصم */}
                                    <p className="text-gray-900 text-3xl font-semibold">
                                        EGP {(+ProdDetails.price - (+ProdDetails.price * +ProdDetails.default_sales_discount) / 100).toFixed()}
                                    </p>
                                    {/* السعر الأصلي مشطوب */}
                                    <p className="text-3xl font-semibold line-through text-gray-500">
                                        EGP {parseInt(ProdDetails.price).toFixed()}
                                    </p>
                                    <span className="bg-mainColor grid items-center justify-center text-black text-sm px-3 py-1 rounded-full">
                                        {t.discount} {ProdDetails.default_sales_discount}%
                                    </span>
                                </>
                            ) : (
                                <p className="text-gray-900 text-3xl font-semibold">
                                    EGP {parseInt(ProdDetails?.price).toFixed()}
                                </p>
                            )}
                        </div>
                        {/* Quantity Controls */}
                        <div className="mt-8">
                            <label className="text-gray-800 text-sm font-medium mb-2 block">{t.quantity}</label>
                            <InputNumber
                                min={1}
                                max={availableStock}
                                value={quantity}
                                onChange={handleQuantityChange}
                                disabled={availableStock === 0}
                                className="w-32"
                                size="large"
                                style={{
                                    borderRadius: "4px",
                                }}
                            />
                        </div>
                        {/* Max quantity warning */}
                        {quantity >= availableStock && availableStock > 0 && (
                            <p className="text-amber-600 text-sm mt-2 font-medium">
{t.maxReached}
                            </p>
                        )}
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-4 mt-4">
                            <button
                                onClick={handleAddToCart}
                                disabled={availableStock === 0}
                                className={`min-w-[200px] px-4 py-2.5 border text-sm font-semibold rounded shadow-md transition-colors ${
                                    availableStock === 0
                                        ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : productInCart
                                        ? "border-green-500 bg-green-600 text-white hover:bg-green-700"
                                        : "border-mainColor bg-transparent text-mainColor hover:bg-mainColor hover:text-black"
                                }`}
                            >
                                {availableStock === 0 ? t.notAvailableBtn : productInCart ? t.addMore : t.addToCart}
                            </button>
                        </div>
                        {/* Product Description */}
                        <div className="mt-8">{renderProductDescription()}</div>
                    </div>
                </div>

                {/* Login Required Modal */}
                <Modal
                    title={t.loginRequired}
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={handleRedirectToSignUp}
                    okText={t.login}
                    cancelText={t.cancel}
                >
                    <p>{t.loginToAdd}</p>
                </Modal>
            </div>
        </div>
    );
};

export default ProductDetails;
